/**
 * Message List Component
 * 
 * Renders the list of chat messages with editing, bookmarking, and branching capabilities.
 * Memoized for performance optimization.
 */

import { memo, RefObject } from "react";

import { Card } from "@/components/ui/card";

import { MessageItem } from "./MessageItem";

import type { Message, ChatUIState } from "@/types/state";

interface MessageListProps {
  messages: Message[];
  bookmarkedMessages: Set<string>;
  uiState: ChatUIState;
  messageRefs: RefObject<{ [key: string]: HTMLDivElement | null }>;
  chatCollapseEnabled: boolean;
  chatCollapsedHeight: number;
  onToggleBookmark: (messageId: string) => void;
  onBranchFromMessage: (message: Message) => void;
  onStartEditing: (message: Message) => void;
  onStartRegenerating: (message: Message) => void;
  onSaveEdit: (messageId: string) => void;
  onCancelEdit: () => void;
  onEditingContentChange: (content: string) => void;
}

const MessageListComponent = ({
  messages,
  bookmarkedMessages,
  uiState,
  messageRefs,
  chatCollapseEnabled,
  chatCollapsedHeight,
  onToggleBookmark,
  onBranchFromMessage,
  onStartEditing,
  onStartRegenerating,
  onSaveEdit,
  onCancelEdit,
  onEditingContentChange,
}: MessageListProps) => {
  if (messages.length === 0) {
    return (
      <Card className="p-8 text-center">
        <p className="text-muted-foreground">
          Start a conversation with your advisory board
        </p>
      </Card>
    );
  }

  return (
    <>
      {messages.map((message) => (
        <MessageItem
          key={message.id}
          message={message}
          isBookmarked={bookmarkedMessages.has(message.id)}
          isEditing={uiState.editingMessageId === message.id}
          editingContent={uiState.editingContent}
          isRegenerating={uiState.regeneratingMessageId === message.id}
          messageRef={(el) => {
            if (messageRefs.current) {
              messageRefs.current[message.id] = el;
            }
          }}
          chatCollapseEnabled={chatCollapseEnabled}
          chatCollapsedHeight={chatCollapsedHeight}
          onToggleBookmark={() => onToggleBookmark(message.id)}
          onBranchFromMessage={() => onBranchFromMessage(message)}
          onStartEditing={() => onStartEditing(message)}
          onStartRegenerating={() => onStartRegenerating(message)}
          onSaveEdit={() => onSaveEdit(message.id)}
          onCancelEdit={onCancelEdit}
          onEditingContentChange={onEditingContentChange}
        />
      ))}
      
      {uiState.sending && (
        <div className="flex justify-start">
          <div className="bg-muted rounded-lg px-4 py-3">
            <p className="text-sm text-muted-foreground">Thinking...</p>
          </div>
        </div>
      )}
    </>
  );
};

export const MessageList = memo(MessageListComponent);
